import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuthenticatedFetch, type Generation } from '../lib/api'
import {
  getCachedGenerations,
  cacheGeneration,
  deleteFromCache,
} from '../lib/cache'

const PAGE_SIZE = 20

interface UseGalleryResult {
  generations: Generation[]
  isLoading: boolean
  error: string | null
  hasMore: boolean
  refresh: () => Promise<void>
  loadMore: () => Promise<void>
  deleteGeneration: (id: string) => Promise<void>
  addGeneration: (generation: Generation) => void
}

export default function useGallery(): UseGalleryResult {
  const authenticatedFetch = useAuthenticatedFetch()
  const [generations, setGenerations] = useState<Generation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const offsetRef = useRef(0)

  const fetchFromApi = useCallback(
    async (offset: number): Promise<Generation[]> => {
      const res = await authenticatedFetch(
        `/api/generations?limit=${PAGE_SIZE}&offset=${offset}`,
      )
      const { data } = (await res.json()) as { data: Generation[] }
      return data
    },
    [authenticatedFetch],
  )

  const refresh = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    offsetRef.current = 0

    try {
      const apiData = await fetchFromApi(0)
      setGenerations(apiData)
      setHasMore(apiData.length >= PAGE_SIZE)
      offsetRef.current = apiData.length

      for (const gen of apiData) {
        cacheGeneration(gen)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load gallery'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }, [fetchFromApi])

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return

    try {
      const apiData = await fetchFromApi(offsetRef.current)
      setGenerations((prev) => [...prev, ...apiData])
      setHasMore(apiData.length >= PAGE_SIZE)
      offsetRef.current += apiData.length

      for (const gen of apiData) {
        cacheGeneration(gen)
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load more'
      setError(message)
    }
  }, [hasMore, isLoading, fetchFromApi])

  const deleteGeneration = useCallback(
    async (id: string) => {
      setGenerations((prev) => prev.filter((g) => g.id !== id))

      try {
        await authenticatedFetch(`/api/generations/${id}`, {
          method: 'DELETE',
        })
        await deleteFromCache(id)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete'
        setError(message)
        refresh()
      }
    },
    [authenticatedFetch, refresh],
  )

  const addGeneration = useCallback((generation: Generation) => {
    setGenerations((prev) => [generation, ...prev])
    cacheGeneration(generation)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function init() {
      try {
        const cached = await getCachedGenerations()
        if (!cancelled && cached.length > 0) {
          setGenerations(cached)
          setIsLoading(false)
        }
      } catch {
        // IndexedDB unavailable, continue to API
      }

      if (!cancelled) {
        refresh()
      }
    }

    init()

    return () => {
      cancelled = true
    }
  }, [refresh])

  return {
    generations,
    isLoading,
    error,
    hasMore,
    refresh,
    loadMore,
    deleteGeneration,
    addGeneration,
  }
}
